/**
 * Persist choice selections when choice set changes. The situation is as follows:
 * - The purchase editor shows a choice set called "Bread"
 * - User selects "Burger"
 * - User changes a parent choice and "Bread" choice set changes.
 * - The new choice set still has the option "Burger" so we need to persist the selection.
 */
export default function translateChoiceSelectionsBetweenItems(
  previousSelections: ChoiceSelections | NestedChoiceSelections,
  previousItem: Item | undefined,
  nextItem: Item,
): ChoiceSelections | NestedChoiceSelections {
  const nextSelections: ChoiceSelections | NestedChoiceSelections = {};

  nextItem?.choiceSets?.forEach(nextSet => {
    if (!('nestedIngredients' in nextSet)) {
      nextSelections[nextSet.key] = [];
    }
  });

  if (!previousSelections) {
    return nextSelections;
  }

  Object.keys(previousSelections).forEach(currentSetKey => {
    const previousSet = previousItem?.choiceSets.find(
      set => set.key === currentSetKey,
    );

    const matchingSet =
      nextItem?.choiceSets.find(set => set.key === previousSet?.key) ??
      nextItem?.choiceSets.find(set => set.id === previousSet?.id) ??
      // hack fix for if reordering an unavailable item and switching to a menu where the menu is available
      nextItem?.choiceSets.find(set => set.id === currentSetKey);

    if (matchingSet && (previousSet || !previousItem)) {
      const isNestedSet =
        'nestedIngredients' in matchingSet &&
        (matchingSet as NestedChoiceSet).nestedIngredients;

      if (isNestedSet) {
        nextSelections[matchingSet.key] = previousSelections[currentSetKey];
      } else {
        // cringe-worthy typescript usage, this supports string ids as selections or duplicated choice objects
        nextSelections[matchingSet.key] = (
          previousSelections[currentSetKey] as (string | ChoiceWithQuantity)[]
        ).map((choiceSelection: string | ChoiceWithQuantity) => {
          return typeof choiceSelection === 'string'
            ? choiceSelection
            : (choiceSelection as ChoiceWithQuantity).id;
        });
      }
    }
  });

  return applyNextSelections(nextItem, nextSelections);
}

/**
 * In the event that the user triggers a new choice set to show and that choice set has preselected choice
 * we need to automatically select that choice.
 * - User changes a parent choice.
 * - A new choice set called "Crumb" is shown in the purchase editor.
 * - This new choice set has a default choice called "Traditional".
 * - We need to mark "Traditional" as selected.
 */
export function applyNextSelections(
  nextItem: Item,
  nextSelections: ChoiceSelections | NestedChoiceSelections,
): ChoiceSelections | NestedChoiceSelections {
  // shallow copy so we don't mutate the incoming object
  const out: ChoiceSelections | NestedChoiceSelections = { ...nextSelections };

  // Heuristic: if any existing value in nextSelections is an object (not an array),
  // assume the nested-object shape is being used.
  const prefersNested = Object.keys(nextSelections ?? {}).some(k => {
    const v = (nextSelections as Record<string, unknown>)[k];
    return v && typeof v === 'object' && !Array.isArray(v);
  });

  (nextItem.choiceSets ?? []).forEach(cs => {
    const key = cs.key ?? cs.id;
    const existing = out[key];

    // If there are already selections for this key (array or object), keep them.
    let hasExisting = false;

    if (Array.isArray(existing)) {
      hasExisting = existing.length > 0;
    } else if (existing && typeof existing === 'object') {
      hasExisting = Object.keys(existing).length > 0;
    } else {
      hasExisting = false;
    }

    if (hasExisting) return;

    // Find any pre-selected choice ids on the choiceSet
    const selectedIds = (cs.choices ?? [])
      .filter(c => !!c && c.selected === true)
      .map(c => c.id);

    // Write the defaults in the same shape we expect:
    if (Array.isArray(existing) || (!existing && !prefersNested)) {
      // array-of-ids shape
      out[key] = selectedIds;
    } else {
      // nested-object shape: map id -> empty object (preserve metadata shape)
      const map: Record<string, unknown> = {};
      selectedIds.forEach(id => {
        map[id] = {};
      });
      out[key] = map as SDict<SDict<ChoiceWithQuantity[]>>;
    }
  });

  return out;
}
